import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useEvidenceDetail,
  useAppendEvidenceEvent,
  type EvidenceEventType,
} from '../api/queries/evidence';
import { ApiError, downloadBlob } from '../api/client';
import { can } from '../auth/store';
import { fmtDateTime } from '../utils/date';
import type { EvidenceChainEvent } from '@stfors/shared-types';
import { LookupSelect } from '../components/LookupSelect';
import { IconAction } from '../components/IconAction';
import { EditEvidenceModal } from '../components/EditEvidenceModal';
import { EditEvidenceEventModal } from '../components/EditEvidenceEventModal';
import { useLookupLabel } from '../api/queries/lookups';
import './EvidenceDetailPage.css';

const TYPE_LABEL: Record<string, string> = {
  mobile_device: 'Mobile device',
  computer: 'Computer',
  hard_drive: 'Hard drive',
  removable_media: 'Removable media',
  vehicle: 'Vehicle',
  cloud_data: 'Cloud data',
  document: 'Document',
  other: 'Other',
};

const STATUS_LABEL: Record<string, string> = {
  received: 'Received',
  imaging: 'Imaging',
  analyzing: 'Analyzing',
  released: 'Released',
  destroyed: 'Destroyed',
  archived: 'Archived',
};

export function EvidenceDetailPage(): JSX.Element {
  const { id: idRaw } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNum = idRaw ? Number(idRaw) : NaN;
  const id = Number.isInteger(idNum) && idNum > 0 ? idNum : null;

  const { data, isPending, error } = useEvidenceDetail(id);
  const [hashCopied, setHashCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EvidenceChainEvent | null>(null);

  const appendEvent = useAppendEvidenceEvent();
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventType, setEventType] = useState<EvidenceEventType>('note');
  const [eventDescription, setEventDescription] = useState('');
  const [eventFrom, setEventFrom] = useState('');
  const [eventTo, setEventTo] = useState('');
  const [eventHash, setEventHash] = useState('');
  const [eventError, setEventError] = useState<string | null>(null);
  const eventLabel = useLookupLabel('evidence_event_type');

  if (id === null) {
    return <p className="ed-status ed-error">Invalid evidence id.</p>;
  }
  if (isPending) return <p className="ed-status">Loading…</p>;
  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return <p className="ed-status ed-error">Evidence not found.</p>;
    }
    if (error instanceof ApiError && error.status === 403) {
      return <p className="ed-status ed-error">You don't have permission to view this evidence.</p>;
    }
    return <p className="ed-status ed-error">Failed to load.</p>;
  }

  const e = data!.evidence;

  const copyHash = async () => {
    if (!e.hash_value) return;
    try {
      await navigator.clipboard.writeText(e.hash_value);
      setHashCopied(true);
      setTimeout(() => setHashCopied(false), 1500);
    } catch {
      /* clipboard might be blocked — silently ignore */
    }
  };

  const onAppendEvent = async (ev: { preventDefault: () => void }) => {
    ev.preventDefault();
    setEventError(null);
    if (!data) return;
    if (eventType === 'imaged' && !/^[0-9a-f]{64}$/.test(eventHash.trim().toLowerCase())) {
      setEventError('Imaged events require a 64-char SHA-256 hex hash.');
      return;
    }
    try {
      await appendEvent.mutateAsync({
        evidence_id: data.evidence.id,
        event_type: eventType,
        description: eventDescription.trim() || undefined,
        from_location: eventFrom.trim() || undefined,
        to_location: eventTo.trim() || undefined,
        hash_value: eventType === 'imaged' ? eventHash.trim().toLowerCase() : undefined,
      });
      setShowEventForm(false);
      setEventDescription('');
      setEventFrom('');
      setEventTo('');
      setEventHash('');
      setEventType('note');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setEventError(body?.error ?? 'Hash conflict — different hash already on file.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setEventError("You don't have permission to add chain-of-custody events.");
        return;
      }
      setEventError('Could not append event. Try again.');
    }
  };

  const onDownloadPdf = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadBlob(
        `/api/evidence/${e.id}/pdf`,
        `${e.chain_of_custody_number}.pdf`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setDownloadError("You don't have permission to export chain-of-custody PDFs.");
      } else {
        setDownloadError('Could not download PDF. Try again.');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ed-grid">
      <header className="ed-header">
        <button className="btn-text" onClick={() => navigate('/evidence')}>
          ← All evidence
        </button>
        <h2>
          <code className="ed-coc">{e.chain_of_custody_number}</code>
        </h2>
        <span className={`ev-status-pill ev-status-${e.evidence_status}`}>
          {STATUS_LABEL[e.evidence_status] ?? e.evidence_status}
        </span>
        {can('evidence.export_pdf') && (
          <button
            type="button"
            className="btn-charcoal-secondary ed-export-btn"
            onClick={onDownloadPdf}
            disabled={downloading}
            title="Export chain-of-custody PDF"
          >
            {downloading ? 'Preparing…' : 'Export PDF'}
          </button>
        )}
        {can('evidence.write') && (
          <IconAction
            action="edit"
            onClick={() => setEditOpen(true)}
            title="Edit evidence entry"
            className="ed-edit-btn"
          />
        )}
      </header>
      {downloadError && (
        <p className="ed-export-error" role="alert">{downloadError}</p>
      )}

      <section className="ed-card">
        <h3>Identification</h3>
        <dl className="ed-defs">
          <dt>Project</dt>
          <dd>
            <Link to={`/projects`} className="ed-link">
              <code>{e.project_case_number ?? `#${e.project_id}`}</code>
            </Link>
          </dd>

          <dt>Type</dt>
          <dd>{TYPE_LABEL[e.evidence_type] ?? e.evidence_type}</dd>

          <dt>Description</dt>
          <dd className="ed-desc">{e.description}</dd>

          <dt>Make / model</dt>
          <dd>{e.make_model ?? <span className="ed-empty">—</span>}</dd>

          <dt>Serial</dt>
          <dd>{e.serial_number ?? <span className="ed-empty">—</span>}</dd>
        </dl>
      </section>

      <section className="ed-card">
        <h3>Custody</h3>
        <dl className="ed-defs">
          <dt>Acquired</dt>
          <dd>{fmtDateTime(e.acquired_date)}</dd>

          <dt>Acquired by</dt>
          <dd>{e.acquired_by_name ?? `User #${e.acquired_by}`}</dd>

          <dt>Owner</dt>
          <dd>{e.owner_name ?? <span className="ed-empty">—</span>}</dd>

          <dt>Client</dt>
          <dd>{e.client_name ?? <span className="ed-empty">—</span>}</dd>

          <dt>Collected from</dt>
          <dd>{e.collected_from ?? <span className="ed-empty">—</span>}</dd>

          <dt>Disposition</dt>
          <dd>{e.disposition ?? <span className="ed-empty">—</span>}</dd>
        </dl>
      </section>

      <section className="ed-card">
        <h3>Storage</h3>
        <dl className="ed-defs">
          <dt>Physical</dt>
          <dd>{e.storage_physical ?? <span className="ed-empty">—</span>}</dd>

          <dt>Digital</dt>
          <dd>
            {e.storage_digital ? (
              <code className="ed-path">{e.storage_digital}</code>
            ) : (
              <span className="ed-empty">—</span>
            )}
          </dd>
        </dl>
      </section>

      <section className="ed-card ed-hash-card">
        <h3>Hash ({e.hash_algorithm.toUpperCase()})</h3>
        {e.hash_value ? (
          <div className="ed-hash-row">
            <code className="ed-hash-value">{e.hash_value}</code>
            <button type="button" className="btn-text" onClick={copyHash}>
              {hashCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        ) : (
          <p className="ed-no-hash">
            Not yet imaged. Hash is set when an <code>imaged</code> event is appended;
            once set it is DB-immutable (admin override available, audited).
          </p>
        )}
      </section>

      <section className="ed-card ed-timeline-card">
        <header className="ed-timeline-header">
          <h3>Chain of Custody — {e.events.length} event{e.events.length === 1 ? '' : 's'}</h3>
          {can('evidence.write') && !showEventForm && (
            <button
              type="button"
              className="btn-charcoal-secondary ed-event-btn"
              onClick={() => setShowEventForm(true)}
            >
              Append event
            </button>
          )}
        </header>

        {showEventForm && (
          <form className="ed-event-form" onSubmit={onAppendEvent}>
            <div className="ed-event-form-row">
              <label className="ed-event-field">
                <span>Event type *</span>
                <LookupSelect
                  category="evidence_event_type"
                  value={eventType}
                  onChange={(v) => setEventType(v as EvidenceEventType)}
                  className="form-input"
                  disabled={appendEvent.isPending}
                />
              </label>
            </div>
            {eventType === 'imaged' && (
              <label className="ed-event-field">
                <span>SHA-256 hash *</span>
                <input
                  type="text"
                  className="form-input ed-event-hash"
                  value={eventHash}
                  onChange={(ev) => setEventHash(ev.target.value)}
                  placeholder="64-char hex (lowercase)"
                  pattern="[0-9a-fA-F]{64}"
                  maxLength={64}
                  required
                  disabled={appendEvent.isPending}
                />
              </label>
            )}
            {(eventType === 'transferred' || eventType === 'released') && (
              <div className="ed-event-form-row">
                <label className="ed-event-field">
                  <span>Transferred from</span>
                  <input
                    type="text"
                    className="form-input"
                    value={eventFrom}
                    onChange={(ev) => setEventFrom(ev.target.value)}
                    disabled={appendEvent.isPending}
                  />
                </label>
                <label className="ed-event-field">
                  <span>Transferred to</span>
                  <input
                    type="text"
                    className="form-input"
                    value={eventTo}
                    onChange={(ev) => setEventTo(ev.target.value)}
                    disabled={appendEvent.isPending}
                  />
                </label>
              </div>
            )}
            <label className="ed-event-field">
              <span>Notes / description</span>
              <textarea
                className="form-input ed-event-textarea"
                value={eventDescription}
                onChange={(ev) => setEventDescription(ev.target.value)}
                rows={3}
                maxLength={10_000}
                disabled={appendEvent.isPending}
              />
            </label>
            {eventError && <p className="ed-event-error" role="alert">{eventError}</p>}
            <div className="ed-event-actions">
              <button
                type="button"
                className="btn-charcoal-secondary"
                onClick={() => {
                  setShowEventForm(false);
                  setEventError(null);
                }}
                disabled={appendEvent.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-cyan-primary"
                disabled={appendEvent.isPending}
              >
                {appendEvent.isPending ? 'Appending…' : 'Append'}
              </button>
            </div>
          </form>
        )}

        <ol className="ed-timeline">
          {e.events.map((ev) => (
            <li key={ev.id} className="ed-event">
              <div className="ed-event-meta">
                <span className={`ed-event-type ed-event-${ev.event_type}`}>
                  {eventLabel(ev.event_type)}
                </span>
                <time className="ed-event-time" dateTime={new Date(ev.event_at).toISOString()}>
                  {fmtDateTime(ev.event_at)}
                </time>
                {can('evidence.write') && (
                  <IconAction
                    action="edit"
                    onClick={() => setEditingEvent(ev)}
                    iconSize={14}
                    title="Edit this event"
                    className="ed-event-edit"
                  />
                )}
              </div>
              <div className="ed-event-actor">
                {ev.actor_name ?? `User #${ev.actor_user_id}`}
              </div>
              {(ev.from_location || ev.to_location) && (
                <div className="ed-event-route">
                  {ev.from_location ?? '—'} → {ev.to_location ?? '—'}
                </div>
              )}
              {ev.description && <p className="ed-event-desc">{ev.description}</p>}
              {ev.attachment_path && (
                <p className="ed-event-attachment">
                  Attachment: <code>{ev.attachment_path}</code>
                </p>
              )}
            </li>
          ))}
        </ol>
      </section>

      {editOpen && (
        <EditEvidenceModal open evidence={e} onClose={() => setEditOpen(false)} />
      )}
      {editingEvent && (
        <EditEvidenceEventModal
          open
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
        />
      )}
    </div>
  );
}
